/**
 * Schema Page
 * Dedicated schema diagram exploration with:
 * 1. Progressive disclosure (macro/mid/micro view levels)
 * 2. Neighbourhood focus (click node → dim others)
 * 3. Crow's Foot relationship notation
 * 4. Relationship panel (click edge → see FK details)
 * 5. Search with highlight
 * 6. Semantic annotations (localStorage, double-click to edit)
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  useViewport,
  type Node,
  type Edge,
} from '@xyflow/react';
import { applyDagreLayout } from '../components/diagram/renderers/shared/layoutEngine';
import type { UnifiedDiagramNode, UnifiedDiagramEdge } from '@veodiagram/shared';
import '@xyflow/react/dist/style.css';
import type { Diagram } from '@veodiagram/shared';
import type { Entity, Relationship, SchemaDiagramData, Cardinality } from '@veodiagram/shared';
import { api } from '../services/api';
import { schemaNodeTypes } from '../components/diagram/renderers/schema/EntityNode';
import { CrowsFootEdge } from '../components/diagram/renderers/schema/CrowsFootEdge';
import { RelationshipPanel } from '../components/diagram/renderers/schema/RelationshipPanel';
import {
  convertSchemaToReactFlow,
  findSearchMatches,
  computeClusters,
  type SchemaCluster,
} from '../components/diagram/renderers/schema/schemaFlowConverter';

// ============= Edge Types =============

const edgeTypes = { crowsFoot: CrowsFootEdge };

// ============= Types =============

type ViewLevel = 'macro' | 'mid' | 'micro';

interface SchemaAnnotations {
  entities: Record<string, { alias?: string; description?: string }>;
}

// ============= Helpers =============

function getAnnotationsKey(projectId: string) {
  return `schema-annotations-${projectId}`;
}

function loadAnnotations(projectId: string): SchemaAnnotations {
  try {
    const raw = localStorage.getItem(getAnnotationsKey(projectId));
    if (raw) return JSON.parse(raw) as SchemaAnnotations;
  } catch { /* ignore */ }
  return { entities: {} };
}

function saveAnnotations(projectId: string, annotations: SchemaAnnotations) {
  try {
    localStorage.setItem(getAnnotationsKey(projectId), JSON.stringify(annotations));
  } catch { /* ignore */ }
}

/**
 * Estimate the rendered width of an EntityNode based on table name length.
 * EntityNode CSS: min-w-[180px] max-w-[280px]
 * ~7px per character + padding for icon, badge, etc.
 */
function estimateEntityWidth(name: string): number {
  const estimated = name.length * 7 + 60;
  return Math.max(180, Math.min(280, estimated));
}

/**
 * Estimate the rendered height of an EntityNode in micro view.
 * Actual measured: header ~38px, column row ~22px, maxVisible=8, footer ~22px.
 */
function estimateEntityHeight(columnCount: number): number {
  const headerHeight = 38;
  const maxVisible = 8;
  const visibleCols = Math.min(columnCount, maxVisible);
  const colRowHeight = 22;
  const footerHeight = columnCount > maxVisible ? 22 : 0;
  const border = 4;
  return headerHeight + visibleCols * colRowHeight + footerHeight + border;
}

/**
 * Find connected components in the entity/relationship graph.
 * Returns arrays of entity IDs grouped by component.
 */
function findConnectedComponents(
  entities: Entity[],
  relationships: Relationship[],
): string[][] {
  const adj = new Map<string, Set<string>>();
  for (const e of entities) adj.set(e.id, new Set());
  for (const r of relationships) {
    adj.get(r.from)?.add(r.to);
    adj.get(r.to)?.add(r.from);
  }

  const visited = new Set<string>();
  const components: string[][] = [];

  for (const e of entities) {
    if (visited.has(e.id)) continue;
    const component: string[] = [];
    const stack = [e.id];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (visited.has(id)) continue;
      visited.add(id);
      component.push(id);
      for (const neighbor of adj.get(id) || []) {
        if (!visited.has(neighbor)) stack.push(neighbor);
      }
    }
    components.push(component);
  }

  // Sort: largest components first
  components.sort((a, b) => b.length - a.length);
  return components;
}

/**
 * Layout schema entities by:
 * 1. Finding connected components
 * 2. Running dagre on each component separately
 * 3. Tiling components in a grid to fill available space
 */
function layoutSchemaEntities(
  entities: Entity[],
  relationships: Relationship[],
  direction: 'LR' | 'TB' | 'RL' | 'BT',
): { positions: Map<string, { x: number; y: number }>; sizes: Map<string, { width: number; height: number }> } {
  const nodeSizeMap = new Map<string, { width: number; height: number }>();
  const entityMap = new Map(entities.map(e => [e.id, e]));

  // Build size map
  for (const e of entities) {
    nodeSizeMap.set(e.id, {
      width: estimateEntityWidth(e.name),
      height: estimateEntityHeight(e.columns.length),
    });
  }

  const components = findConnectedComponents(entities, relationships);
  const componentBounds: Array<{ ids: string[]; positions: Map<string, { x: number; y: number }>; w: number; h: number }> = [];

  for (const compIds of components) {
    const compEntities = compIds.map(id => entityMap.get(id)!).filter(Boolean);
    const compIdSet = new Set(compIds);
    const compRels = relationships.filter(r => compIdSet.has(r.from) && compIdSet.has(r.to));

    if (compIds.length === 1) {
      // Single node — no layout needed
      const size = nodeSizeMap.get(compIds[0])!;
      componentBounds.push({
        ids: compIds,
        positions: new Map([[compIds[0], { x: 0, y: 0 }]]),
        w: size.width,
        h: size.height,
      });
      continue;
    }

    // Build dagre nodes/edges for this component
    const layoutNodes: UnifiedDiagramNode[] = compEntities.map(e => {
      const size = nodeSizeMap.get(e.id)!;
      return {
        id: e.id,
        type: 'service' as const,
        label: e.name,
        position: { x: 0, y: 0 },
        width: size.width,
        height: size.height,
      };
    });
    const layoutEdges: UnifiedDiagramEdge[] = compRels.map(r => ({
      id: r.id,
      source: r.from,
      target: r.to,
      type: 'default' as const,
      animated: false,
    }));

    const result = applyDagreLayout(layoutNodes, layoutEdges, {
      direction,
      nodeSpacing: 60,
      rankSpacing: 80,
    });

    // Normalize positions to start at (0, 0)
    const positions = new Map<string, { x: number; y: number }>();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of result) {
      const size = nodeSizeMap.get(n.id)!;
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + size.width);
      maxY = Math.max(maxY, n.position.y + size.height);
    }
    for (const n of result) {
      positions.set(n.id, { x: n.position.x - minX, y: n.position.y - minY });
    }

    componentBounds.push({
      ids: compIds,
      positions,
      w: maxX - minX,
      h: maxY - minY,
    });
  }

  // Tile components in a grid layout
  const gap = 80;
  const posMap = new Map<string, { x: number; y: number }>();

  // Use a simple row-based packing: place components left-to-right, wrapping rows
  // Target a roughly square aspect ratio
  const totalArea = componentBounds.reduce((sum, c) => sum + (c.w + gap) * (c.h + gap), 0);
  const targetWidth = Math.max(1200, Math.sqrt(totalArea) * 1.4);

  let curX = 0;
  let curY = 0;
  let rowHeight = 0;

  for (const comp of componentBounds) {
    // Wrap to next row if this component would exceed target width
    if (curX > 0 && curX + comp.w > targetWidth) {
      curX = 0;
      curY += rowHeight + gap;
      rowHeight = 0;
    }

    // Place all nodes in this component at offset (curX, curY)
    for (const id of comp.ids) {
      const localPos = comp.positions.get(id)!;
      posMap.set(id, { x: curX + localPos.x, y: curY + localPos.y });
    }

    curX += comp.w + gap;
    rowHeight = Math.max(rowHeight, comp.h);
  }

  return { positions: posMap, sizes: nodeSizeMap };
}

/**
 * Assign sourceHandle/targetHandle on edges based on relative positions of source/target nodes.
 * This prevents edges from routing through intermediate nodes.
 */
function assignHandlesByPosition(
  edges: Edge[],
  positionMap: Map<string, { x: number; y: number }>,
  nodeSizeMap: Map<string, { width: number; height: number }>,
): Edge[] {
  return edges.map(edge => {
    const sourcePos = positionMap.get(edge.source);
    const targetPos = positionMap.get(edge.target);
    if (!sourcePos || !targetPos) return edge;

    // Use center points for comparison
    const sourceSize = nodeSizeMap.get(edge.source) || { width: 240, height: 100 };
    const targetSize = nodeSizeMap.get(edge.target) || { width: 240, height: 100 };
    const srcCX = sourcePos.x + sourceSize.width / 2;
    const srcCY = sourcePos.y + sourceSize.height / 2;
    const tgtCX = targetPos.x + targetSize.width / 2;
    const tgtCY = targetPos.y + targetSize.height / 2;

    const dx = tgtCX - srcCX;
    const dy = tgtCY - srcCY;

    let sourceHandle: string;
    let targetHandle: string;

    // Pick handles based on dominant direction
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal dominant
      if (dx > 0) {
        sourceHandle = 'right-src';
        targetHandle = 'left';
      } else {
        sourceHandle = 'left-src';
        targetHandle = 'right';
      }
    } else {
      // Vertical dominant
      if (dy > 0) {
        sourceHandle = 'bottom-src';
        targetHandle = 'top';
      } else {
        sourceHandle = 'top-src';
        targetHandle = 'bottom';
      }
    }

    return { ...edge, sourceHandle, targetHandle };
  });
}

/**
 * Reconstruct SchemaDiagramData from the stored DiagramGraph format.
 * The API converts SchemaDiagramData to the generic graph format for storage.
 */
function convertStoredGraphToSchemaData(storedGraph: Record<string, unknown>): SchemaDiagramData | null {
  const rawNodes = storedGraph.nodes as Array<Record<string, unknown>> | undefined;
  const rawEdges = storedGraph.edges as Array<Record<string, unknown>> | undefined;

  if (!rawNodes) return null;

  const entities: Entity[] = rawNodes
    .filter(n => n.type === 'database')
    .map((node, index) => {
      const nodeData = (node.data || {}) as Record<string, unknown>;
      const cols = (nodeData.columns as Entity['columns']) || [];
      return {
        id: node.id as string,
        name: (node.label as string) || (node.id as string),
        tableName: nodeData.tableName as string | undefined,
        columns: cols,
        primaryKey: cols.filter(c => c.isPrimaryKey).map(c => c.name),
        description: node.description as string | undefined,
        sourceFile: nodeData.sourceFile as string | undefined,
        sourceType: nodeData.sourceType as Entity['sourceType'] | undefined,
        evidence: nodeData.evidence as Entity['evidence'],
        position: (node.position as { x: number; y: number }) || {
          x: (index % 4) * 320,
          y: Math.floor(index / 4) * 280,
        },
      };
    });

  const relationships: Relationship[] = (rawEdges || [])
    .filter(e => e.type === 'database')
    .map(edge => {
      const edgeData = (edge.data || {}) as Record<string, unknown>;
      return {
        id: edge.id as string,
        from: edge.source as string,
        to: edge.target as string,
        fromColumn: edgeData.fromColumn as string | undefined,
        toColumn: edgeData.toColumn as string | undefined,
        cardinality: (edgeData.cardinality as Cardinality) || '1:N',
        label: edge.label as string | undefined,
        isRequired: (edgeData.isRequired as boolean) || false,
        onDelete: edgeData.onDelete as Relationship['onDelete'],
        onUpdate: edgeData.onUpdate as Relationship['onUpdate'],
      };
    });

  return {
    metadata: { title: 'Database Schema' },
    entities,
    relationships,
  };
}

// ============= Zoom-level auto view-level hook =============

function useZoomViewLevel(manualLevel: ViewLevel, zoom: number): ViewLevel {
  if (zoom < 0.2) return 'macro';
  if (zoom < 0.45) return 'mid';
  return manualLevel;
}

// ============= Inner component (inside ReactFlowProvider) =============

interface SchemaCanvasProps {
  schemaData: SchemaDiagramData;
  projectId: string;
  onSelectedEdge: (edgeId: string | null) => void;
}

function SchemaCanvas({ schemaData, projectId, onSelectedEdge }: SchemaCanvasProps) {
  const reactFlowInstance = useReactFlow();
  // Keep a ref so effects can call fitView without adding reactFlowInstance to deps
  // (useReactFlow() returns a new object reference on every render in React Flow v12)
  const fitViewRef = useRef(reactFlowInstance.fitView);
  useEffect(() => { fitViewRef.current = reactFlowInstance.fitView; });
  const { zoom } = useViewport();

  const [viewLevel, setViewLevel] = useState<ViewLevel>('micro');
  const [layoutDirection, setLayoutDirection] = useState<'LR' | 'TB' | 'RL' | 'BT'>('TB');
  const [focusedEntityId, setFocusedEntityId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [annotations, setAnnotations] = useState<SchemaAnnotations>(() => loadAnnotations(projectId));
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Resolve effective view level based on zoom
  const effectiveViewLevel = useZoomViewLevel(viewLevel, zoom);

  // Compute search matches
  const { entityIds: searchHighlightIds, matches: searchMatches } = useMemo(
    () => findSearchMatches(searchQuery, schemaData.entities),
    [searchQuery, schemaData.entities]
  );

  // Compute clusters for stats
  const clusters = useMemo(
    () => computeClusters(schemaData.entities, schemaData.relationships),
    [schemaData.entities, schemaData.relationships]
  );

  // Handle annotation change
  const handleAnnotationChange = useCallback((entityId: string, alias: string, description: string) => {
    setAnnotations(prev => {
      const next: SchemaAnnotations = {
        entities: {
          ...prev.entities,
          [entityId]: { alias, description },
        },
      };
      saveAnnotations(projectId, next);
      return next;
    });
  }, [projectId]);

  // Handle edge click — wire through data prop
  const handleEdgeClick = useCallback((edgeId: string) => {
    onSelectedEdge(edgeId);
  }, [onSelectedEdge]);

  // Convert to React Flow format
  const { nodes: baseNodes, edges: baseEdges } = useMemo(() => convertSchemaToReactFlow(schemaData, {
    viewLevel: effectiveViewLevel,
    focusedEntityId,
    searchHighlightIds,
    showAnnotations,
    annotations: annotations.entities,
    onAnnotationChange: handleAnnotationChange,
    onEdgeClick: handleEdgeClick,
  }), [
    schemaData, effectiveViewLevel, focusedEntityId, searchHighlightIds,
    showAnnotations, annotations.entities, handleAnnotationChange, handleEdgeClick,
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState(baseNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(baseEdges);

  // Stable layout positions from dagre. Reset when schemaData or direction changes.
  const [layoutPositions, setLayoutPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  // Store node sizes so we can assign handles after layout
  const [layoutNodeSizes, setLayoutNodeSizes] = useState<Map<string, { width: number; height: number }>>(new Map());
  const layoutKeyRef = useRef<string>('');

  useEffect(() => {
    const key = `${schemaData.metadata?.title ?? 'schema'}-${schemaData.entities.length}-${layoutDirection}`;
    if (layoutKeyRef.current === key) return;
    layoutKeyRef.current = key;

    const { positions, sizes } = layoutSchemaEntities(
      schemaData.entities,
      schemaData.relationships,
      layoutDirection,
    );

    setLayoutPositions(positions);
    setLayoutNodeSizes(sizes);
  }, [schemaData, layoutDirection]);

  // When view-level / focus / search changes, update visual props but preserve current positions
  useEffect(() => {
    setNodes(prev => baseNodes.map(n => {
      const existing = prev.find(p => p.id === n.id);
      return existing ? { ...n, position: existing.position } : n;
    }));
  }, [baseNodes, setNodes]);

  // When dagre positions are (re)computed, apply them and fit view once
  useEffect(() => {
    if (layoutPositions.size === 0) return;
    setNodes(prev => prev.map(n => {
      const pos = layoutPositions.get(n.id);
      return pos ? { ...n, position: pos } : n;
    }));
    setTimeout(() => fitViewRef.current({ padding: 0.15, duration: 400 }), 50);
  }, [layoutPositions, setNodes]);

  // Fix 2: Assign handles on edges based on layout positions
  useEffect(() => {
    if (layoutPositions.size === 0) return;
    setEdges(assignHandlesByPosition(baseEdges, layoutPositions, layoutNodeSizes));
  }, [baseEdges, layoutPositions, layoutNodeSizes, setEdges]);

  // Handle node click → neighbourhood focus
  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setFocusedEntityId(prev => prev === node.id ? null : node.id);
  }, []);

  // Clear focus on background click
  const handlePaneClick = useCallback(() => {
    setFocusedEntityId(null);
    onSelectedEdge(null);
  }, [onSelectedEdge]);

  // Jump to entity by ID
  const jumpToEntity = useCallback((entityId: string) => {
    const node = nodes.find(n => n.id === entityId);
    if (node) {
      reactFlowInstance.fitView({
        nodes: [node],
        padding: 0.5,
        duration: 400,
      });
    }
  }, [nodes, reactFlowInstance]);

  // Search result click
  const handleSearchResultClick = useCallback((entityId: string) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    jumpToEntity(entityId);
  }, [jumpToEntity]);

  // Detect source type for stats
  const primarySource = schemaData.entities[0]?.sourceType || 'unknown';

  return (
    <div className="flex flex-col overflow-hidden h-full">
      {/* Stats bar */}
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex items-center gap-4">
        <span className="font-medium text-slate-700">{schemaData.entities.length} tables</span>
        <span>•</span>
        <span>{schemaData.relationships.length} relationships</span>
        {clusters.length > 0 && (
          <>
            <span>•</span>
            <span>{clusters.length} clusters</span>
          </>
        )}
        <span>•</span>
        <span>Source: <span className="font-medium capitalize">{primarySource}</span></span>
      </div>

      {/* Toolbar */}
      <div className="px-4 py-2 bg-white border-b border-slate-200 flex flex-wrap items-center gap-3">
        {/* View level toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500 mr-1">Detail:</span>
          {(['macro', 'mid', 'micro'] as ViewLevel[]).map(level => (
            <button
              key={level}
              onClick={() => setViewLevel(level)}
              className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                viewLevel === level
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              title={level === 'macro' ? 'Table names only' : level === 'mid' ? 'Keys only' : 'Full detail'}
            >
              {level === 'macro' ? 'Table Names' : level === 'mid' ? 'Keys Only' : 'Full Detail'}
            </button>
          ))}
          {zoom < 0.7 && (
            <span className="text-[10px] text-slate-400 ml-1 italic">
              (auto: {effectiveViewLevel})
            </span>
          )}
        </div>

        {/* Search */}
        <div ref={searchRef} className="relative flex-1 min-w-[160px] max-w-xs">
          <div className="relative">
            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setShowSearchDropdown(e.target.value.trim().length > 0);
              }}
              onFocus={() => searchQuery.trim() && setShowSearchDropdown(true)}
              onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
              placeholder="Search tables or columns..."
              className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setShowSearchDropdown(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Search dropdown */}
          {showSearchDropdown && searchMatches.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
              {searchMatches.slice(0, 10).map((match, i) => (
                <button
                  key={`${match.entityId}-${i}`}
                  onMouseDown={() => handleSearchResultClick(match.entityId)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2"
                >
                  <svg className="w-3 h-3 text-indigo-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  <span className="font-medium text-slate-700 truncate">{match.entityName}</span>
                  {match.columnName && (
                    <span className="text-slate-400 shrink-0">— column: <span className="text-slate-600">{match.columnName}</span></span>
                  )}
                </button>
              ))}
            </div>
          )}
          {showSearchDropdown && searchQuery.trim() && searchMatches.length === 0 && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-50 px-3 py-2 text-xs text-slate-400">
              No results
            </div>
          )}
        </div>

        {/* Annotations toggle */}
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showAnnotations}
            onChange={e => setShowAnnotations(e.target.checked)}
            className="rounded"
          />
          Annotations
        </label>

        {/* Focus indicator */}
        {focusedEntityId && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 rounded text-xs text-indigo-600">
            <span>Focused</span>
            <button
              onClick={() => setFocusedEntityId(null)}
              className="text-indigo-400 hover:text-indigo-600"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Layout direction */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500 mr-1">Layout:</span>
          {([
            { value: 'LR', label: '→', title: 'Left to Right' },
            { value: 'TB', label: '↓', title: 'Top to Bottom' },
            { value: 'RL', label: '←', title: 'Right to Left' },
            { value: 'BT', label: '↑', title: 'Bottom to Top' },
          ] as const).map(({ value, label, title }) => (
            <button
              key={value}
              onClick={() => setLayoutDirection(value)}
              title={title}
              className={`px-2 py-1 text-xs rounded font-mono font-bold transition-colors ${
                layoutDirection === value
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Fit view */}
        <button
          onClick={() => reactFlowInstance.fitView({ padding: 0.15 })}
          className="px-2.5 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
        >
          Fit View
        </button>
      </div>

      {/* Tip */}
      {focusedEntityId === null && (
        <div className="px-4 py-1 bg-slate-50 border-b border-slate-100 text-[11px] text-slate-400 italic">
          Click a table to focus its relationships • Click an edge to inspect FK details • Double-click a table header to add annotation
        </div>
      )}

      {/* React Flow Canvas */}
      <div className="flex-1 overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          nodeTypes={schemaNodeTypes}
          edgeTypes={edgeTypes}
          className="bg-slate-50"
          minZoom={0.1}
          maxZoom={2}
        >
          <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
          <Controls />
          <MiniMap
            nodeStrokeWidth={3}
            {...{ pannable: true, zoomable: true } as object}
            style={{ width: 160, height: 100 }}
            className="bg-white border border-slate-200 rounded-md"
          />
        </ReactFlow>
      </div>
    </div>
  );
}

// ============= Cluster Legend =============

function ClusterLegend({ clusters }: { clusters: SchemaCluster[] }) {
  if (clusters.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {clusters.map(cluster => (
        <div
          key={cluster.id}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs"
          style={{ backgroundColor: cluster.color }}
        >
          <span className="font-medium text-slate-700">{cluster.label}</span>
          <span className="text-slate-500">({cluster.entityIds.length})</span>
        </div>
      ))}
    </div>
  );
}

// ============= Main SchemaPage =============

export function SchemaPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [_diagram, setDiagram] = useState<Diagram | null>(null);
  const [schemaData, setSchemaData] = useState<SchemaDiagramData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Load schema data
  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const diagrams = await api.getDiagrams(projectId);
        const schemaMeta = diagrams.find(d => d.type === 'databaseSchema' && d.isLatest !== false);

        if (!schemaMeta) {
          if (!cancelled) setError('No schema diagram found. Run analysis to generate one.');
          return;
        }

        // #1040: list endpoint is metadata-only post-#1034. Fetch the full
        // diagram (with `graph`) by ID — single-diagram GET still includes it.
        const schemaDiagram = await api.getDiagram(schemaMeta.id);
        if (!cancelled) setDiagram(schemaDiagram);

        const storedGraph = schemaDiagram.graph as Record<string, unknown>;
        if (!storedGraph || !storedGraph.nodes) {
          if (!cancelled) setError('Schema data is incomplete. Try regenerating the diagram.');
          return;
        }

        const data = convertStoredGraphToSchemaData(storedGraph);
        if (!data || data.entities.length === 0) {
          if (!cancelled) setError('Failed to parse schema data. Try regenerating the diagram.');
          return;
        }

        if (!cancelled) setSchemaData(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load schema');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [projectId]);

  // Get selected relationship
  const selectedRelationship = useMemo(() => {
    if (!selectedEdgeId || !schemaData) return null;
    return schemaData.relationships.find(r => r.id === selectedEdgeId) || null;
  }, [selectedEdgeId, schemaData]);

  // Clusters for legend
  const clusters = useMemo(
    () => schemaData ? computeClusters(schemaData.entities, schemaData.relationships) : [],
    [schemaData]
  );

  // Jump to entity (for RelationshipPanel — we pass this down)
  const handleJumpToEntity = useCallback((_entityId: string) => {
    // The actual scrolling is done inside SchemaCanvas
    // This callback is just a pass-through for the panel
    setSelectedEdgeId(null);
  }, []);

  // ---- Render loading/error ----
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading schema...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-sm">
          <svg className="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
          <p className="text-slate-600 mb-4">{error}</p>
          <Link
            to={`/projects/${projectId}`}
            className="text-sm text-indigo-600 hover:underline"
          >
            ← Back to project
          </Link>
        </div>
      </div>
    );
  }

  if (!schemaData) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Page Header */}
      <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3 bg-white shrink-0">
        <Link
          to={`/projects/${projectId}`}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
          <h1 className="text-base font-semibold text-slate-800">Database Schema</h1>
        </div>
        {clusters.length > 0 && (
          <div className="ml-4 hidden sm:block">
            <ClusterLegend clusters={clusters} />
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Canvas */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <ReactFlowProvider>
            <SchemaCanvas
              schemaData={schemaData}
              projectId={projectId!}
              onSelectedEdge={setSelectedEdgeId}
            />
          </ReactFlowProvider>
        </div>

        {/* Relationship Panel slide-out */}
        {selectedEdgeId && selectedRelationship && (
          <div className="absolute bottom-4 right-4 z-20">
            <RelationshipPanel
              relationship={selectedRelationship}
              entities={schemaData.entities}
              onClose={() => setSelectedEdgeId(null)}
              onJumpToEntity={handleJumpToEntity}
            />
          </div>
        )}
      </div>
    </div>
  );
}
